import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import RefreshButton from '../components/RefreshButton';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { backupService } from '../services/backup.service';
import { useAuthStore } from '@/stores/authStore';
import { userService } from '../services/user.service';
import type { BackupLog, JobFileResult } from '../types/backup.types';
import type { User } from '@/types/user.types';
import { useErrorStore } from '@/stores/errorStore';

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
    case 'cancelled':
      return 'error';
    case 'pending':
      return 'warning';
    case 'running':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusText = (status: string): string => {
  switch (status) {
    case 'success':
      return '成功';
    case 'failed':
      return '失敗';
    case 'pending':
      return '等待中';
    case 'running':
      return '執行中';
    case 'cancelled':
      return '已取消';
    default:
      return status;
  }
};

// 格式化檔案大小
const formatSize = (bytes: number | undefined | null): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) {
    return '-';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// 格式化時間
const formatTime = (timeStr: string | undefined | null): string => {
  if (!timeStr) return '-';
  try {
    const date = new Date(timeStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return timeStr;
  }
};

// 格式化執行時間
const formatDuration = (seconds: number | undefined | null): string => {
  if (seconds === undefined || seconds === null || seconds === 0) {
    return '-';
  }
  if (seconds < 60) {
    return `${seconds} 秒`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins} 分 ${secs} 秒`;
};

// job_file_results.status → 標籤（ADR-045）。與後端 watcher.FileResultStatuses 對齊。
const JOB_FILE_STATUS: Record<string, { label: string; color: 'default' | 'success' | 'error' | 'warning' | 'info' }> = {
  backed_up: { label: '已備份', color: 'success' },
  failed: { label: '失敗', color: 'error' },
  skip: { label: '跳過（無變更）', color: 'default' },
  dedup: { label: '重複（僅建立記錄）', color: 'info' },
  reject: { label: '拒絕（Hash 不一致）', color: 'warning' },
  backup: { label: '待上傳（舊版 agent 回報）', color: 'default' },
};

// 明細對話框一次載入的每檔結果上限（後端 limit 上限 200）
const JOB_FILES_PAGE_SIZE = 200;

export default function Logs() {
  const showSuccess = useErrorStore((s) => s.showSuccess);
  const showError = useErrorStore((s) => s.showError);
  const currentUser = useAuthStore((s) => s.user);
  // Mirrors backend RequireRole("admin","sub_admin") on the backup-jobs management group
  // (user filter + DELETE /backup-jobs/failed) — A-35/F-29
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'sub_admin';
  const [logs, setLogs] = useState<BackupLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [userFilter, setUserFilter] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [detailLog, setDetailLog] = useState<BackupLog | null>(null);

  // 每檔結果（ADR-045）：明細開啟時載入 GET /backup-jobs/:id/files。
  // 舊版 UI 讀的 details[] 後端從未回傳（A-31 / E-26）。
  const [jobFiles, setJobFiles] = useState<JobFileResult[]>([]);
  const [jobFilesTotal, setJobFilesTotal] = useState(0);
  const [jobFilesLoading, setJobFilesLoading] = useState(false);
  const detailJobId = detailLog?.id;

  useEffect(() => {
    if (!detailJobId) {
      setJobFiles([]);
      setJobFilesTotal(0);
      return;
    }
    let cancelled = false;
    setJobFilesLoading(true);
    backupService
      .getJobFiles(detailJobId, { limit: JOB_FILES_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setJobFiles(page.items);
        setJobFilesTotal(page.pagination.total);
      })
      .catch((error) => {
        console.error('Failed to load job files:', error);
        if (cancelled) return;
        setJobFiles([]);
        setJobFilesTotal(0);
      })
      .finally(() => {
        if (!cancelled) setJobFilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailJobId]);

  // 取消進行中的備份（A-06 / P-2）：只有 running 可取消，後端其餘回 400
  const [cancelTarget, setCancelTarget] = useState<BackupLog | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // 分頁狀態 (server-side; backend GET /backup-jobs default limit=25)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);

  useEffect(() => {
    if (canManage) {
      userService.listUsers({ page_size: 100 }).then((res) => {
        setAvailableUsers(res?.items ?? []);
      }).catch(console.error);
    }
  }, [canManage]);

  const handleCancelJob = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await backupService.cancelJob(cancelTarget.id);
      showSuccess('已取消備份工作');
      setCancelTarget(null);
      loadLogs();
    } catch (error) {
      console.error('Failed to cancel backup job:', error);
      showError('取消失敗：工作可能已經結束，請重新整理');
    } finally {
      setCancelling(false);
    }
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await backupService.getLogs({
        user_id: userFilter || undefined,
        page: page + 1, // MUI 從 0 開始，API 從 1 開始
        limit: rowsPerPage,
      });
      setLogs(data.items);
      setTotalLogs(data.pagination.total);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  }, [userFilter, page, rowsPerPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const failedCount = logs.filter((log) => log.status === 'failed').length;

  const handleClearFailed = async () => {
    try {
      setClearing(true);
      const result = await backupService.clearFailedJobs();
      setClearDialogOpen(false);
      showSuccess(`已成功清除 ${result.count} 筆失敗記錄`);
      loadLogs();
    } catch (error) {
      console.error('Failed to clear failed jobs:', error);
      showError('清除失敗記錄時發生錯誤');
    } finally {
      setClearing(false);
    }
  };

  const columns = useMemo<GridColDef<BackupLog>[]>(
    () => [
      {
        field: 'status',
        headerName: '狀態',
        flex: 0.7,
        minWidth: 80,
        type: 'singleSelect',
        valueOptions: ['success', 'failed', 'running', 'pending', 'cancelled'],
        renderCell: (params) => (
          <Chip
            label={getStatusText(params.value)}
            color={getStatusColor(params.value)}
            size="small"
          />
        ),
      },
      {
        field: 'created_at',
        headerName: '備份時間',
        flex: 1.3,
        minWidth: 140,
        valueGetter: (_value: string, row: BackupLog) => row.started_at || row.created_at,
        valueFormatter: (value: string) => formatTime(value),
      },
      {
        field: 'total_files',
        headerName: '檔案數量',
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const log = params.row;
          const total = log.total_files || 0;
          if (total === 0) return '-';
          const success = log.success_files || 0;
          const failed = log.failed_files || 0;
          const rejected = log.rejected_files || 0;
          if (failed > 0 || rejected > 0) {
            const issues: string[] = [];
            if (failed > 0) issues.push(`${failed} 失敗`);
            if (rejected > 0) issues.push(`${rejected} 拒絕`);
            return `${success}/${total} (${issues.join(', ')})`;
          }
          return `${total} 個檔案`;
        },
      },
      {
        field: 'total_size_bytes',
        headerName: '總大小',
        flex: 0.8,
        minWidth: 90,
        valueFormatter: (value: number) => formatSize(value),
      },
      {
        field: 'duration_seconds',
        headerName: '執行時間',
        flex: 0.8,
        minWidth: 80,
        valueFormatter: (value: number) => formatDuration(value),
      },
      {
        field: 'actions',
        headerName: '詳情',
        flex: 0.5,
        minWidth: 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {params.row.error_message && (
              <Tooltip title={params.row.error_message} arrow>
                <ErrorOutlineIcon color="error" fontSize="small" />
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setDetailLog(params.row)}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
            {canManage && params.row.status === 'running' && (
              <Tooltip title="取消這個進行中的備份" arrow>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="取消備份工作"
                  onClick={() => setCancelTarget(params.row)}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [canManage],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            備份記錄
          </Typography>
          <Typography variant="body2" color="text.secondary">
            查看所有自動備份紀錄
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canManage && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>使用者篩選</InputLabel>
              <Select
                value={userFilter}
                label="使用者篩選"
                onChange={(e) => {
                  setUserFilter(e.target.value);
                  setPage(0); // 篩選變更時回到第一頁
                }}
              >
                <MenuItem value="">全部使用者</MenuItem>
                {availableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canManage && failedCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setClearDialogOpen(true)}
              disabled={loading}
              size="small"
            >
              清除失敗記錄 ({failedCount})
            </Button>
          )}
          <RefreshButton onClick={loadLogs} loading={loading} />
        </Box>
      </Box>

      {/* 清除確認對話框 */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => !clearing && setClearDialogOpen(false)}
      >
        <DialogTitle>確認清除失敗記錄</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要清除所有 {failedCount} 筆失敗的備份記錄嗎？此操作無法復原。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} disabled={clearing}>
            取消
          </Button>
          <Button
            onClick={handleClearFailed}
            color="error"
            variant="contained"
            disabled={clearing}
            startIcon={clearing ? <CircularProgress size={16} /> : undefined}
          >
            {clearing ? '清除中...' : '確認清除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 取消進行中備份的確認對話框 */}
      <Dialog open={cancelTarget !== null} onClose={() => !cancelling && setCancelTarget(null)}>
        <DialogTitle>確認取消備份</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要取消這個進行中的備份嗎？已上傳的檔案會保留，工作狀態會標記為「已取消」。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>
            返回
          </Button>
          <Button
            onClick={handleCancelJob}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : undefined}
          >
            {cancelling ? '取消中...' : '確認取消'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 詳情對話框 */}
      <Dialog
        open={detailLog !== null}
        onClose={() => setDetailLog(null)}
        maxWidth="md"
        fullWidth
      >
        {detailLog && (
          <>
            <DialogTitle>備份任務詳情</DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium', width: 150 }}>任務 ID</TableCell>
                    <TableCell><code style={{ fontSize: '0.85em' }}>{detailLog.id}</code></TableCell>
                  </TableRow>
                  {detailLog.worker_job_id && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'medium' }}>Worker 任務 ID</TableCell>
                      <TableCell><code style={{ fontSize: '0.85em' }}>{detailLog.worker_job_id}</code></TableCell>
                    </TableRow>
                  )}
                  {detailLog.source_paths && detailLog.source_paths.length > 0 && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'medium' }}>來源路徑</TableCell>
                      <TableCell>
                        {detailLog.source_paths.map((p) => (
                          <Box key={p} component="code" sx={{ display: 'block', fontSize: '0.85em', wordBreak: 'break-all' }}>
                            {p}
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>狀態</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(detailLog.status)}
                        color={getStatusColor(detailLog.status)}
                        size="small"
                        sx={{ minWidth: 70 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>開始時間</TableCell>
                    <TableCell>{formatTime(detailLog.started_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>完成時間</TableCell>
                    <TableCell>{formatTime(detailLog.completed_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>執行時間</TableCell>
                    <TableCell>{formatDuration(detailLog.duration_seconds)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>檔案統計</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ display: 'block' }}>
                        總共 {detailLog.total_files || 0} 個，
                        新備份 {detailLog.success_files || 0} 個，
                        跳過（無變更）{detailLog.skipped_files || 0} 個，
                        重複檔案（內容相同）{detailLog.deduplicated_files || 0} 個，
                        失敗 {detailLog.failed_files || 0} 個，
                        拒絕 {detailLog.rejected_files || 0} 個
                      </Box>
                      {(detailLog.skipped_files || 0) > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.85em' }}>
                          跳過：同檔名且內容相同，已備份過
                        </Box>
                      )}
                      {(detailLog.deduplicated_files || 0) > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'info.main', fontSize: '0.85em' }}>
                          重複檔案：不同檔名但內容相同，僅建立記錄不重複儲存
                        </Box>
                      )}
                      {(detailLog.rejected_files || 0) > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontSize: '0.85em' }}>
                          拒絕：同檔名但內容已變更（Hash 不同）
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'medium' }}>總大小</TableCell>
                    <TableCell>{formatSize(detailLog.total_size_bytes)}</TableCell>
                  </TableRow>
                  {detailLog.error_message && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'medium', color: 'error.main' }}>錯誤訊息</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>{detailLog.error_message}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Typography variant="subtitle2" gutterBottom component="div" sx={{ mt: 2 }}>
                檔案清單{jobFilesTotal > 0 ? `（${jobFilesTotal} 筆）` : ''}
              </Typography>
              {jobFilesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">載入中…</Typography>
                </Box>
              ) : jobFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  此任務沒有每檔結果：任務尚未處理到檔案，或執行它的 file-watcher 版本不回報每檔結果。
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>檔案路徑</TableCell>
                        <TableCell>Hash (SHA-256)</TableCell>
                        <TableCell>大小</TableCell>
                        <TableCell>狀態</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobFiles.map((f) => {
                        const s = JOB_FILE_STATUS[f.status] ?? { label: f.status, color: 'default' as const };
                        return (
                          <TableRow key={f.id}>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{f.file_path}</TableCell>
                            <TableCell>
                              <code style={{ fontSize: '0.85em' }} title={f.file_hash}>
                                {f.file_hash.slice(0, 12)}…
                              </code>
                            </TableCell>
                            <TableCell>{formatSize(f.file_size)}</TableCell>
                            <TableCell>
                              <Chip label={s.label} color={s.color} size="small" />
                              {f.reason && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                  {f.reason}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {jobFilesTotal > jobFiles.length && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                      僅顯示前 {jobFiles.length} 筆，共 {jobFilesTotal} 筆。
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailLog(null)}>關閉</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          備份執行歷史
        </Typography>

        <DataGrid
          rows={logs}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={totalLogs}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          localeText={{ noRowsLabel: '暫無備份記錄' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{ border: 'none', '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }, '& .MuiDataGrid-cell[data-field="actions"]': { justifyContent: 'flex-end' } }}
        />
      </Paper>
    </Box>
  );
}
